import { isNumber } from "lodash-es";
import { type FC, type MouseEventHandler, type Ref, useRef } from "react";
import { useController } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { AddPlusIcon, SubtractMinusIcon } from "mds/icons";

type NumberFieldWithStepperButtonsProps = {
  className?: string;
  variant?: "default" | "compact";
  ref?: Ref<HTMLInputElement>;
  name: string;
  label?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  disabled?: boolean;
  onStep?: () => void;
  // set the `progressive` option on the useForm hook to have RHF pass these automatically
  max?: string | number;
  min?: string | number;
};

export const NumberFieldWithStepperButtons: FC<NumberFieldWithStepperButtonsProps> = ({
  className,
  variant = "default",
  ref,
  label,
  onStep,
  ...props
}) => {
  const {
    field,
    fieldState: { error },
  } = useController(props);
  const isCompact = variant === "compact";
  const controlRef = useRef<HTMLDivElement>(null);
  const disabled = props.disabled;
  const value = parseInt(field.value as string, 10);
  const max = props.max !== undefined ? Number(props.max) : undefined;
  const min = props.min !== undefined ? Number(props.min) : undefined;
  const isNaNValue = Number.isNaN(value);
  const keepFocusOnStep: MouseEventHandler<HTMLButtonElement> = (event) => {
    // A blur save can disable the stepper before its click updates the value.
    // Keep focus inside autosaving controls until the click has saved the step.
    if (
      onStep &&
      event.button === 0 &&
      controlRef.current?.contains(event.currentTarget.ownerDocument.activeElement)
    ) {
      event.preventDefault();
    }
  };
  const step = (nextValue: number) => {
    field.onChange(nextValue);
    onStep?.();
  };

  return (
    <div
      className={twMerge(
        "group flex h-[32px] items-stretch gap-2",
        isCompact && "h-7 gap-1",
        className,
      )}
      data-disabled={disabled}
      ref={controlRef}
      role="spinbutton"
    >
      <div
        className={twMerge(
          "order-2 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-black-tint-90 bg-white p-2 text-sm font-medium text-black-tint-10 group-data-[disabled]:cursor-not-allowed group-data-[disabled]:bg-black-tint-93 group-data-[disabled]:text-black-tint-70",
          isCompact && "gap-1 rounded-md bg-black-tint-99 px-2 py-1 font-normal",
        )}
      >
        <input
          aria-describedby={props["aria-describedby"]}
          aria-invalid={error ? "true" : "false"}
          aria-label={props["aria-label"]}
          className="w-6 text-right"
          inputMode="numeric"
          pattern="[0-9]*"
          type="text"
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") {
              e.preventDefault();
              if (!disabled && !isNaNValue && (!isNumber(max) || value < max)) {
                step(value + 1);
              }
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!disabled && !isNaNValue && (!isNumber(min) || value > min)) {
                step(value - 1);
              }
            }
          }}
          {...field}
          ref={ref}
        />
        {label && <span className="select-none">{label}</span>}
      </div>

      <div className={isCompact ? "order-1" : "contents"}>
        <SquareIconButton
          aria-label={t("common.decrement")}
          className="order-1"
          disabled={disabled || isNaNValue || (isNumber(min) && value <= min)}
          kind="tertiary"
          size={isCompact ? "xs" : "s"}
          onClick={() => step(value - 1)}
          onMouseDown={keepFocusOnStep}
        >
          <SubtractMinusIcon />
        </SquareIconButton>
      </div>

      <div className={isCompact ? "order-3" : "contents"}>
        <SquareIconButton
          aria-label={t("common.increment")}
          className="order-3"
          disabled={disabled || isNaNValue || (isNumber(max) && value >= max)}
          kind="tertiary"
          size={isCompact ? "xs" : "s"}
          onClick={() => step(value + 1)}
          onMouseDown={keepFocusOnStep}
        >
          <AddPlusIcon />
        </SquareIconButton>
      </div>
    </div>
  );
};
